import { EditOutlined, EyeOutlined } from '@ant-design/icons'
import type { IncidentListReturnState } from '@features/logs/hooks/useIncidentListReturn'
import type { Carer, FollowUpActionStatus } from '@lmc/ts-common'
import { Avatar, Button, Table } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Modal from '@lmc/ui-kit/src/legacy/molecules/Modal'
import { showToast } from 'common/utils'
import { getCarer, getCarerName } from 'hooks/useFindItem/useCarers'
import useIncidentActionStatus from 'hooks/useIncidentReport/useIncidentActionStatus'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import type React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { getStatusLabel } from '../../../utils/incidentActionsUtils'
import UpdateActionsModal from './UpdateActionsModal'

interface ActionsTableProps {
  actionsData: any
  isLoading: boolean
  currentPage: number
  itemsPerPage: number
  onPageChange?: (page: number, size?: number) => void
  testId?: string
}

const ActionsTable: React.FC<ActionsTableProps> = ({
  actionsData,
  isLoading,
  currentPage,
  itemsPerPage,
  onPageChange,
  testId = 'actions-table'
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const navigate = useNavigate()
  const { search } = useLocation()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [selectedOwner, setSelectedOwner] = useState<string>('')

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const { updateActionStatus } = useIncidentActionStatus({
    onUpdateSuccess: () => {
      setIsModalVisible(false)
    }
  })

  const handleEditClick = (record: any) => {
    setSelectedRecord(record)
    setSelectedStatus(record.apiStatus || '')
    setSelectedOwner(record.ownedBy?.id || '')
    setIsModalVisible(true)
  }

  const handleSubmit = async () => {
    try {
      await updateActionStatus({
        incidentReportId: selectedRecord.incidentReportId,
        actionId: selectedRecord.actionId,
        status: selectedStatus as FollowUpActionStatus,
        ownerId: selectedOwner
      })
    } catch (error) {
      showToast({
        title: 'Error updating incident action',
        type: 'error'
      })
    }
  }

  const handleViewClick = (record: any) => {
    navigate(`/reports/incidents/actions/${record.logId}`, {
      state: { listSearch: search } satisfies IncidentListReturnState
    })
  }

  const tableData =
    actionsData?.data?.map((item: any) => {
      const assignedCarer = item.assignedUserId
        ? getCarer(carers, item.assignedUserId)
        : null

      return {
        key: item.actionId,
        logId: item.logId,
        incidentType: item.categoryItem,
        incidentReportId: item.incidentReportId,
        actionId: item.actionId,
        title: item.title,
        status: getStatusLabel(item.status, t),
        apiStatus: item.status,
        ownedBy: assignedCarer
          ? {
              id: assignedCarer.id,
              name: getCarerName(assignedCarer),
              avatar: assignedCarer.picture
            }
          : null
      }
    }) || []

  const columns = [
    {
      title: t(incidentReportsConstants.INCIDENT_TITLE),
      dataIndex: 'incidentType',
      key: 'incidentType'
    },
    {
      title: t(incidentReportsConstants.ACTION),
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: t(incidentReportsConstants.STATUS),
      dataIndex: 'status',
      key: 'status'
    },
    {
      title: t(incidentReportsConstants.OWNED_BY),
      dataIndex: 'ownedBy',
      key: 'ownedBy',
      render: (_: any, record: any) => {
        if (!record.ownedBy) {
          return (
            <UnassignedText
              data-testid={`${testId}-row-${record.key}-unassigned`}
            >
              {t(incidentReportsConstants.UNASSIGNED)}
            </UnassignedText>
          )
        }
        return (
          <OwnerContainer
            data-testid={`${testId}-row-${record.key}-owner-container`}
          >
            <Avatar
              url={record.ownedBy.avatar}
              size="small"
              data-testid={`${testId}-row-${record.key}-avatar`}
            />
            <Text
              weight="medium"
              data-testid={`${testId}-row-${record.key}-owner-name`}
            >
              {record.ownedBy.name}
            </Text>
          </OwnerContainer>
        )
      }
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_: any, record: any) => (
        <ActionsContainer
          data-testid={`${testId}-row-${record.key}-actions-container`}
        >
          <ActionButton
            variant="text"
            onClick={() => handleEditClick(record)}
            data-testid={`${testId}-row-${record.key}-edit-button`}
          >
            <StyledEditIcon />
          </ActionButton>
          <ActionButton
            variant="text"
            onClick={() => handleViewClick(record)}
            data-testid={`${testId}-row-${record.key}-view-button`}
          >
            <StyledEyeIcon />
          </ActionButton>
        </ActionsContainer>
      )
    }
  ]

  return (
    <>
      <TableContainer data-testid={`${testId}-container`}>
        <StyledTable
          data-testid={testId}
          columns={columns}
          dataSource={tableData}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: itemsPerPage,
            total: actionsData?.pagination?.totalItems || 0,
            showSizeChanger: true,
            position: ['bottomCenter'],
            onChange: onPageChange,
            onShowSizeChange: onPageChange
          }}
          scroll={{ x: 800 }}
        />
      </TableContainer>

      <Modal
        data-testid={`${testId}-modal`}
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        onCancel={() => setIsModalVisible(false)}
        localisation={{
          confirmText: 'Save Changes',
          cancelText: 'Cancel'
        }}
        onConfirm={handleSubmit}
        width580px={true}
      >
        <UpdateActionsModal
          data-testid={`${testId}-update-modal`}
          selectedStatus={selectedStatus}
          selectedOwner={selectedOwner}
          onStatusChange={setSelectedStatus}
          onOwnerChange={setSelectedOwner}
        />
      </Modal>
    </>
  )
}

const TableContainer = styled.div`
  border-radius: 16px;
`

const StyledTable = styled(Table)`
  .ant-table-tbody > tr:nth-child(even) > td {
    background-color: ${({ theme }) => theme.color.neutral.color11};
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background-color: ${({ theme }) => theme.color.neutral.color10};
  }

  .ant-table-tbody > tr:nth-child(even):hover > td {
    background-color: ${({ theme }) => theme.color.neutral.color11} !important;
  }

  .ant-table-tbody > tr:nth-child(odd):hover > td {
    background-color: ${({ theme }) => theme.color.neutral.color10} !important;
  }
`

const OwnerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const ActionButton = styled(Button)`
  color: ${({ theme }) => theme.color.neutral.color5};
  width: 48px;
  height: 48px;

  &:focus,
  &:active {
    color: ${({ theme }) => theme.color.neutral.color5};
  }

  &:hover {
    /* This should be more specific than teh focus and active states to prevent focus trap highlight -- we don't want that */
    color: ${({ theme }) => theme.color.neutral.color3};
  }
`

const StyledEyeIcon = styled(EyeOutlined)`
  font-size: 22px;
`

const StyledEditIcon = styled(EditOutlined)`
  font-size: 22px;
`

const UnassignedText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

export default ActionsTable
